import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { Layout } from 'antd';
import { useParams, useHistory } from 'react-router-dom';
import useGlobal from '../../../store/Store';
import { PdfContainer } from '../../../common/pdf/PdfContainer';
import ResultSideBar from './ResultSideBar';
import { PdfFile, TestResult, TestResultRow } from '../../../types';
import { getTestResultFiles, getTestResultFileDetails } from '../../../lib/api/aws-api';
import { getDatasetFileVersion, getDatasetFileVersions, getDataset } from '../../../lib/api/dataset-api';
import { getInboxOrResultFileLabels } from '../../../lib/labels';
import { processTestResult } from '../../../lib/util';
import { reformTestResultFileToPdfFile } from '../../../lib/util/reform';
import { TopBar } from '../../../components/layout/TopBar';
import { DetailContainer } from '../../../components/layout/DetailContainer';
import {Dataset, DatasetFileVersion} from "../../../types/dataset";

const { Content } = Layout;

const DetailTestResult = () => {
  let history = useHistory();

  const { fileId, datasetVersionId, algorithmVersionId } = useParams() as {
    fileId: string;
    algorithmVersionId: string;
    datasetVersionId: string;
  };
  const [testResult, setTestResult] = useState<null | TestResult>(null);
  const [pdfFile, setPdfFile] = useState<PdfFile>();
  const [globalState, globalActions] = useGlobal();
  const [prevId, setPrevId] = useState<null | string>(null);
  const [nextId, setNextId] = useState<null | string>(null);
  const [dataset, setDataset] = useState<null | Dataset>(null);
  const [datasetFileVersion, setDatasetFileVersion] = useState<null | DatasetFileVersion>(null);

  const reset = useCallback(() => {
    globalActions.setLabelOptions(null);
    globalActions.setLabelEdit(null);
    globalActions.removeLabels();
    setTestResult(null);
  }, [globalActions, setTestResult]);

  useEffect(() => {
    const fetchData = async () => {
      if (algorithmVersionId && datasetVersionId) {
        const versions = await getDatasetFileVersions(fileId);
        const datasetFileVersion = await getDatasetFileVersion(versions[0].id);

        const dataset = await getDataset(datasetFileVersion.datasetVersion.dataset_id);
        setDatasetFileVersion(datasetFileVersion);
        setDataset(dataset);
        let file = await getTestResultFileDetails(algorithmVersionId, datasetVersionId, fileId);

        if (file) {
          const testResultFile = processTestResult(file);
          const labels = getInboxOrResultFileLabels(testResultFile);
          globalActions.setLabels(labels);
          setTestResult(testResultFile);
          setPdfFile(reformTestResultFileToPdfFile(datasetFileVersion, testResultFile));
        }

        const allFiles = await getTestResultFiles(algorithmVersionId, datasetVersionId);
        const sortedFiles = allFiles.sort((a, b) => a.file_id.localeCompare(b.file_id));
        const index = sortedFiles.findIndex(
          (it: TestResultRow) => it.file_id === datasetFileVersion.datasetFile.file_id
        );

        console.log(sortedFiles);

        let prevId = null;
        let nextId = null;
        if (index > 0) {
          for (var i = index - 1; i > -1; i--) {
            prevId = sortedFiles[i].file_id;
            break;
          }
        }
        if (index < sortedFiles.length - 1) {
          for (let i = index + 1; i < sortedFiles.length; i++) {
            nextId = sortedFiles[i].file_id;
            break;
          }
        }

        setPrevId(prevId);
        setNextId(nextId);
      }
    };

    reset();
    fetchData();
  }, [fileId, globalActions, algorithmVersionId, datasetVersionId, reset]);

  const toDatasetFile = ()=>{
    if(dataset)
    history.push(`/projects/${dataset.project_id}/datasets/${dataset.id}/dataset-file/${datasetFileVersion?.id}`);
  }

  const keyPress = useCallback(
      (event: any): void => {
        if (event.code === 'ArrowRight' && nextId) {
          history.push(`/overview/${algorithmVersionId}/datasets/${datasetVersionId}/dataset-file/${nextId}`);
        }
        if (event.code === 'ArrowLeft' && prevId) {
          history.push(`/overview/${algorithmVersionId}/datasets/${datasetVersionId}/dataset-file/${prevId}`);
        }
      },
      [nextId, prevId, history, algorithmVersionId, datasetVersionId]
  );

  useEffect(() => {
    window.addEventListener('keyup', keyPress);
    return () => {
      window.removeEventListener('keyup', keyPress);
    };
  }, [nextId,prevId, keyPress]);

  const { labels } = globalState;
  const filteredLabels = useMemo(() => labels.filter(label => label.exists || !label.reliable), [labels]);

  return (
    <>
      <TopBar
        title={(pdfFile && pdfFile.id) || ''}
        closeFunction={() => {
          history.push(`/overview/${algorithmVersionId}/datasets/${datasetVersionId}`);
        }}
        prevId={prevId}
        nextId={nextId}
        baseUrl={`/overview/${algorithmVersionId}/datasets/${datasetVersionId}/dataset-file`}
      />
      <DetailContainer>
        <Layout>
          <Content>
            <div className={'pdf-layout'} style={{ width: '100%' }}>
              <PdfContainer
                file={pdfFile}
                labels={filteredLabels}
                showNonExisting={true}
                onAnnotationChange={() => {}}
                withCorrections={true}
              />
            </div>
          </Content>
        </Layout>
        <ResultSideBar testResult={testResult} toFile={toDatasetFile} datasetFileVersion={datasetFileVersion}/>
      </DetailContainer>
    </>
  );
};

export default DetailTestResult;
